// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { CalendarIcon, Settings } from 'lucide-react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { adminRecordInspector } from 'rails_routes'

import { type EnrollmentEvent } from '../types'
import { cn, convertToUTCDate, getDateString, getFormattedDate } from '../utils'
import CreateEnrollmentEventSheet from './CreateEnrollmentEventSheet'
import Badge from './reusable/Badge'
import ButtonPrimary from './reusable/ButtonPrimary'
import TanstackTable from './reusable/table/TanstackTable'
import Tooltip from './reusable/Tooltip'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Toggle } from './ui/toggle'

type EnrollmentEventsProps = {
  employee_id: string
}

type UpdateEnrollmentEventInput = {
  enrollment_window_start_date?: string
  enrollment_window_end_date?: string
  status?: string
}

const BASE_FINCH_URL = '/api/web/admin/enrollment_event'

const EnrollmentEventsContext = createContext<{
  eventActiveInPlanYear: { [key: number]: boolean }
  isEditing: boolean
  updateEnrollmentEvent: (
  id: string,
  updates: UpdateEnrollmentEventInput
  ) => void
}>({
      eventActiveInPlanYear: {},
      isEditing: false,
      updateEnrollmentEvent: () => {},
    })

const columnHelper = createColumnHelper<EnrollmentEvent>()

const CannotActivateTooltip = ({ children }: { children: React.ReactNode }) => {
  return (
    <Tooltip triggerChildren={children}>
      <div
        style={{ zIndex: 100, width: '12rem' }}
        className='text-xs p-2 w-60 rounded-md text-center'
      >
        Cannot activate multiple events in the same plan year
      </div>
    </Tooltip>
  )
}

const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    cell: (info) => (
      <a
        className='text-sm font-normal'
        href={adminRecordInspector.show({ id: info.getValue() })}
        target='_blank'
        rel='noreferrer'
      >
        {info.getValue().slice(-5)}
      </a>
    ),
    header: () => <div className='text-sm font-normal'>ID</div>,
  }),
  columnHelper.accessor('enrollment_type', {
    id: 'enrollment_type',
    cell: (info) => (
      <div className='text-sm font-normal'>{info.getValue()}</div>
    ),
    header: () => <div className='text-sm font-normal'>Type</div>,
  }),
  columnHelper.accessor('plan_year', {
    id: 'plan_year',
    cell: (info) => (
      <div className='text-sm font-normal'>{info.getValue()}</div>
    ),
    header: () => <div className='text-sm font-normal'>Plan year</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      switch (info.getValue()) {
        case 'inactive':
          return <Badge text='Inactive' type='default' />
        case 'completed':
          return <Badge text='Completed' type='success' />
        case 'in_progress':
          return <Badge text='In Progress' type='highlight' />
        case 'active':
          return <Badge text='Active' type='attention' />
        default:
          return <div className='text-sm font-normal'>NA</div>
      }
    },
    header: () => <div className='text-sm font-normal'>Status</div>,
  }),
  columnHelper.accessor('enrollment_window_start_date', {
    id: 'enrollment_window_start_date',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent } = useContext(
        EnrollmentEventsContext
      )

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>
            {getFormattedDate(getValue(), { timeZone: 'UTC' })}
          </div>
        )
      }

      if (row.original.status === 'active') {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn(
                  'w-auto pl-3 text-left font-normal rounded-md px-2 text-xs',
                  !getValue() && 'text-muted-foreground'
                )}
              >
                {getValue()
                  ? (
                      getFormattedDate(getValue(), { timeZone: 'UTC' })
                    )
                  : (
                    <span>Pick a date</span>
                    )}
                <CalendarIcon className='pl-1 ml-auto h-4 w-4 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                selected={convertToUTCDate(new Date(getValue()))}
                onSelect={(date) => {
                  if (!date) return
                  updateEnrollmentEvent(row.original.id, {
                    enrollment_window_start_date: getDateString(date),
                  })
                }}
                disabled={false}
                autoFocus
                defaultMonth={new Date(getValue())}
              />
            </PopoverContent>
          </Popover>
        )
      }

      return <div className='text-sm font-normal'>{getValue()}</div>
    },
    header: () => <div className='text-sm font-normal'>Start date</div>,
  }),
  columnHelper.accessor('enrollment_window_end_date', {
    id: 'enrollment_window_end_date',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent } = useContext(
        EnrollmentEventsContext
      )

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>
            {getFormattedDate(getValue(), { timeZone: 'UTC' })}
          </div>
        )
      }

      if (row.original.status === 'active') {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn(
                  'w-auto pl-3 text-left font-normal rounded-md px-2 text-xs',
                  !getValue() && 'text-muted-foreground'
                )}
              >
                {getValue()
                  ? (
                      getFormattedDate(getValue(), { timeZone: 'UTC' })
                    )
                  : (
                    <span>Pick a date</span>
                    )}
                <CalendarIcon className='pl-1 ml-auto h-4 w-4 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                selected={convertToUTCDate(new Date(getValue()))}
                onSelect={(date) => {
                  if (!date) return
                  updateEnrollmentEvent(row.original.id, {
                    enrollment_window_end_date: getDateString(date),
                  })
                }}
                disabled={false}
                initialFocus
                defaultMonth={new Date(getValue())}
              />
            </PopoverContent>
          </Popover>
        )
      }

      return <div className='text-sm font-normal'>{getValue()}</div>
    },
    header: () => <div className='text-sm font-normal'>End date</div>,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row }) => {
      const { eventActiveInPlanYear, isEditing, updateEnrollmentEvent } =
        useContext(EnrollmentEventsContext)
      const { plan_year: planYear, status } = row.original

      if (!isEditing) {
        return <div className='text-sm font-normal'>NA</div>
      }

      // Do not allow the activation of past year's events.
      if (row.original.plan_year < new Date().getFullYear()) {
        return <div className='text-sm font-normal'>NA</div>
      }

      if (status === 'active') {
        return (
          <ButtonPrimary
            onClick={() => {
              updateEnrollmentEvent(row.original.id, { status: 'inactive' })
            }}
            styles={{ width: '6rem', height: 'auto', padding: '6px' }}
          >
            Deactivate
          </ButtonPrimary>
        )
      }

      if (eventActiveInPlanYear[planYear]) {
        // Add Tooltip saying you can't activate this event because another event is active in this plan year.
        return (
          <CannotActivateTooltip>
            <ButtonPrimary
              styles={{ width: '6rem', height: 'auto', padding: '6px' }}
              isDisabled
              onClick={() => {}}
            >
              Activate
            </ButtonPrimary>
          </CannotActivateTooltip>
        )
      }

      return (
        <ButtonPrimary
          onClick={() => {
            updateEnrollmentEvent(row.original.id, { status: 'active' })
          }}
          styles={{ width: '6rem', height: 'auto', padding: '6px' }}
        >
          Activate
        </ButtonPrimary>
      )
    },
    header: () => <div className='text-sm font-normal'>Action</div>,
  }),
]

function EnrollmentEvents ({ employee_id: employeeId }: EnrollmentEventsProps) {
  const [data, setData] = useState<EnrollmentEvent[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<{ message: string }>({ message: '' })

  const [isEditing, setIsEditing] = useState<boolean>(false)

  const [showCreateEvent, setShowCreateEvent] = useState(false)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetAll: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
      sorting: [
        { id: 'plan_year', desc: true },
        { id: 'enrollment_window_start_date', desc: true },
      ],
    },
  })

  const updateEnrollmentEvent = async (
    id: string,
    updates: UpdateEnrollmentEventInput
  ) => {
    const params = new URLSearchParams([['employee_id', employeeId]]).toString()
    // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
    const request = new FetchRequest(
      'put',
      `${BASE_FINCH_URL}/${id}?${params}`,
      {
        body: {
          ...updates,
        },
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    )

    const response = await request.perform()

    if (response.ok) {
      toast.success('Enrollment event updated!')
      fetchEmployeeEnrollmentEvents()
      setError({ message: '' })
      return
    }
    const { message } = await response.json

    setError({ message })
  }

  const fetchEmployeeEnrollmentEvents = async () => {
    try {
      setIsLoading(true)

      const params = new URLSearchParams([
        ['employee_id', employeeId],
      ]).toString()

      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(`${BASE_FINCH_URL}?${params}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        throw Error('Failed to fetch employee enrollment events options')
      }

      const json = await response.json()
      const { data }: { data: EnrollmentEvent[] } = json

      setData(data)
    } catch (e) {
      setError({
        message:
          'Failed to load employee enrollment events. Please try again. If you continue to see this error, please contact support.',
      })
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchEmployeeEnrollmentEvents()
  }, [])

  const eventActiveInPlanYear = data.reduce<{ [key: number]: boolean }>(
    (acc, { plan_year: planYear, status }) => {
      if (status === 'active') {
        acc[planYear] = true
      }
      return acc
    },
    {}
  )

  return (
    <EnrollmentEventsContext.Provider
      value={{ eventActiveInPlanYear, isEditing, updateEnrollmentEvent }}
    >
      <div className='mt-4 flex justify-between items-center'>
        <Toggle
          variant='outline'
          onPressedChange={setIsEditing}
          pressed={isEditing}
        >
          <Settings />
          <span className='ml-2'>Edit Events</span>
        </Toggle>
        <Button onClick={() => setShowCreateEvent(true)}>Create Event</Button>
      </div>
      <div className='mt-4'>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          paginationLabelSingular='enrollment event'
        />
        <div>
          {error.message && <div className='text-red-500'>{error.message}</div>}
        </div>
      </div>
      <CreateEnrollmentEventSheet
        employeeId={employeeId}
        isOpen={showCreateEvent}
        onClose={() => setShowCreateEvent(false)}
        onEventCreated={() => fetchEmployeeEnrollmentEvents()}
      />
    </EnrollmentEventsContext.Provider>
  )
}

export default EnrollmentEvents
